import { type AddressType } from '@sg/garnish';

import { type FeatureFlags, useFeatureFlag } from '@order/LaunchDarkly';

/**
 * Returns whether this order has tipping enabled.
 */
export const useTippingEnabled = (params: UseTippingEnabledParams): boolean => {
  const { restaurantId, orderChannel, availableTips, maxTipAmount } = params;

  const locationsWithEnabledTipping = useFeatureFlag(
    'CELS-1149-short-target-tipping-by-location-enabled',
  );

  const listOfLocationsWithEnabledTipping =
    getListOfLocationsWithEnabledTipping(locationsWithEnabledTipping);

  const isTippingEnabledForAllLocations = checkIfTippingEnabledForAllStores(
    listOfLocationsWithEnabledTipping,
  );

  const isTippingEnabledForLocation =
    isTippingEnabledForAllLocations ||
    listOfLocationsWithEnabledTipping.includes(restaurantId);

  // ─────────────────────────────────────────────────────────────────────

  if (orderChannel === 'pickup') {
    return maxTipAmount > 0 && isTippingEnabledForLocation && availableTips.length > 0; // prettier-ignore
  }

  if (orderChannel === 'delivery') {
    return maxTipAmount > 0 && availableTips.length > 0;
  }

  if (orderChannel === 'outpost') {
    return maxTipAmount > 0 && availableTips.length > 0;
  }

  return false;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getListOfLocationsWithEnabledTipping(
  maybeTargetTippingStores: unknown,
): TargetTippingStores {
  const isValidListProvided = Array.isArray(maybeTargetTippingStores);

  if (!isValidListProvided) {
    return [];
  }

  return maybeTargetTippingStores.map(String);
}

/**
 * Returns a `boolean` indicating whether tipping is enabled for all stores,
 * by checking if the first list item is equal to 'all'.
 */
function checkIfTippingEnabledForAllStores(
  targetTippingStores: readonly string[],
): boolean {
  return targetTippingStores[0] === 'all';
}

// ─── Types ──────────────────────────────────────────────────────────────────

type UseTippingEnabledParams = {
  restaurantId: string;
  orderChannel: AddressType;
  availableTips: ReadonlyArray<number | 'CUSTOM'>;
  maxTipAmount: number;
};

type TargetTippingStores =
  FeatureFlags['CELS-1149-short-target-tipping-by-location-enabled'];
